import { useCallback, useRef, useState } from 'react';
import { config } from '../config';
import { runOneTrial } from '../harness/runner';
import { errorMessage } from '../app/errors';
import type { TrialSample } from '../types';

/**
 * Real taps, on real glass.
 *
 * Auto mode anchors on the moment before dispatch. That excludes the leg between the finger
 * touching the screen and JavaScript running — which NFR-1's budget does *not* exclude, and which
 * only a real device can tell us. This pad reproduces design §5.1's tap path as closely as a
 * harness can:
 *
 *   pointerdown  → resolve the target from the element, apply the pressed class (CSS only — a
 *                  receipt of touch, making no data claim), promote to its own compositor layer
 *   pointerup    → capture the anchor timestamp, dispatch the same two-row `readwrite`
 *   complete     → toggle the ack class imperatively on the element ref, inside the measured
 *                  window, so the DOM work is counted
 *   next frame   → the earliest moment that ack can reach the glass
 *
 * There is no third "pending" visual state, per design §5.3 — pressed, then flash.
 *
 * ── Open-ended, on purpose ──────────────────────────────────────────────────
 * The pad has no tap target. It shipped with one — a literal `30` — while admissibility is gated
 * on `HARNESS_TRIALS_MIN` (50), so this mode could never return anything but `inconclusive` on the
 * one measurement carrying real touch input. A 31st tap would not have been the fix either: a real
 * match produces hundreds of taps across ninety minutes, so any fixed count models nothing. The
 * operator taps for as long as they like and decides when to stop; the pad shows how many more are
 * needed for an admissible verdict, and the only bound is `HARNESS_LIVE_TAP_MAX`, a safety stop so
 * a pad left face-up in a pocket cannot grow a run without end.
 *
 * Finishing below the floor is allowed and keeps every sample. The run is recorded, marked
 * inadmissible with the reason in words, and its taps are still in the exported JSON — throwing
 * away real touch data because there was not enough of it would be the worse trade.
 */

export interface LiveTapPadProps {
  hypothesisId: string;
  matchId: string;
  /** Called on every tap, so the lease can watch for a foreign load appearing mid-session. */
  onTap?: () => void;
  onComplete: (samples: TrialSample[], failures: { index: number; message: string }[]) => void;
  onCancel: () => void;
}

export function LiveTapPad({
  hypothesisId,
  matchId,
  onTap,
  onComplete,
  onCancel,
}: LiveTapPadProps) {
  const padRef = useRef<HTMLDivElement | null>(null);
  const samplesRef = useRef<TrialSample[]>([]);
  const failuresRef = useRef<{ index: number; message: string }[]>([]);
  const busyRef = useRef(false);

  const [count, setCount] = useState(0);
  const [lastMs, setLastMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onPointerDown = useCallback(() => {
    padRef.current?.classList.add('pressed');
  }, []);

  const onPointerCancel = useCallback(() => {
    padRef.current?.classList.remove('pressed');
  }, []);

  const onPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const pad = padRef.current;
      pad?.classList.remove('pressed');

      // Single-flight (design §5.2): a tap arriving while one is in flight is dropped rather than
      // overlapping transactions, which would make both timings meaningless.
      if (busyRef.current) return;
      // The safety stop. Not a target — the pad simply stops recording, and the samples already
      // taken remain intact and finishable.
      if (samplesRef.current.length >= config.HARNESS_LIVE_TAP_MAX) return;
      busyRef.current = true;
      onTap?.();

      // `event.timeStamp` is on the same monotonic base as `performance.now()` in Safari, so this
      // is the true moment the finger left the glass — not the moment React handed us the event.
      const anchor = event.timeStamp;
      const index = samplesRef.current.length;

      void (async () => {
        try {
          const sample = await runOneTrial(index, anchor, 0, {
            hypothesisId,
            matchId,
            now: () => performance.now(),
            onCommitted: () => {
              // Inside the measured window, exactly where design §5.1 step 9 puts it.
              pad?.classList.add('ack');
            },
          });
          samplesRef.current.push(sample);
          setCount(samplesRef.current.length);
          setLastMs(sample.totalMs);

          setTimeout(() => pad?.classList.remove('ack'), config.ACK_PERSIST_MIN_MS);
        } catch (cause) {
          const message = errorMessage(cause);
          failuresRef.current.push({ index, message });
          setError(message);
        } finally {
          busyRef.current = false;
        }
      })();
    },
    [hypothesisId, matchId, onTap],
  );

  const finish = useCallback(() => {
    onComplete([...samplesRef.current], [...failuresRef.current]);
  }, [onComplete]);

  const needed = Math.max(0, config.HARNESS_TRIALS_MIN - count);
  const admissible = needed === 0;
  const atCap = count >= config.HARNESS_LIVE_TAP_MAX;

  return (
    <div className="card">
      <h2>Live taps</h2>
      <p className="sub">
        Tap for as long as you like, at the pace you would actually count a pattern — then finish.
        Anchored on the real <code>pointerup</code>, so this is the only run that includes the
        touch-to-JavaScript latency auto mode cannot see. It is the run NFR-1 is judged on.
      </p>
      {error && <div className="error">{error}</div>}
      <div
        ref={padRef}
        className="tap-pad"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onPointerLeave={onPointerCancel}
        role="button"
        tabIndex={0}
        aria-label={
          admissible
            ? `Tap to record a measurement. ${count} taps recorded; enough for a verdict.`
            : `Tap to record a measurement. ${count} taps recorded; ${needed} more needed for a verdict.`
        }
      >
        <div className="count">{count}</div>
        <div className="hint">
          {atCap
            ? `cap reached (${config.HARNESS_LIVE_TAP_MAX})`
            : admissible
              ? 'enough for a verdict — keep going or finish'
              : `${needed} more for a verdict`}
          {lastMs !== null && ` · last ${Math.round(lastMs)} ms`}
        </div>
      </div>

      <div className="row two" style={{ marginTop: 12 }}>
        <button className={admissible ? 'primary' : ''} onClick={finish} disabled={count === 0}>
          {admissible ? 'Finish' : `Finish anyway (${count})`}
        </button>
        <button className="danger" onClick={onCancel}>
          Discard
        </button>
      </div>
      {!admissible && count > 0 && (
        <p className="note">
          Finishing now keeps all {count} taps, but the run will be marked inadmissible: a tail
          figure from fewer than {config.HARNESS_TRIALS_MIN} taps is not a tail figure.
        </p>
      )}
    </div>
  );
}
